// WalletPage — Wallet overview
// Combines balance, deposit, withdraw, transaction history
// Layout:
//   - Top nav: < Back | Wallet
//   - Hero card: Available balance + pending + total
//   - Quick actions: Deposit / Withdraw / Activate Challenge
//   - Payment methods (cards)
//   - Transactions list

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Txn {
  id: string;
  kind: 'deposit' | 'withdraw' | 'challenge' | 'reward' | 'payout';
  title: string;
  sub: string;
  amount: number;       // + or -
  time: string;         // ISO
  status: 'completed' | 'pending' | 'failed';
}

const DEMO_TXNS: Txn[] = [
  { id: '1', kind: 'withdraw', title: 'Withdrawal to Chase Bank',       sub: '****4521',            amount: -1200.00, time: '2026-07-22T14:30:00Z', status: 'pending' },
  { id: '2', kind: 'payout',    title: 'Funded payout — 80% split',     sub: 'Beginner $50K',        amount: 920.00,   time: '2026-07-20T09:15:00Z', status: 'completed' },
  { id: '3', kind: 'challenge', title: 'Beginner $100K challenge',      sub: 'Order ord-7',          amount: -149.00,  time: '2026-07-18T12:00:00Z', status: 'completed' },
  { id: '4', kind: 'reward',    title: 'Platform activation bonus',    sub: 'Claimed',              amount: 30.00,    time: '2026-07-15T16:45:00Z', status: 'completed' },
  { id: '5', kind: 'payout',    title: 'Funded payout — 80% split',     sub: 'Beginner $50K',        amount: 425.00,   time: '2026-07-12T10:30:00Z', status: 'completed' },
  { id: '6', kind: 'deposit',   title: 'Card deposit',                  sub: 'Visa ****4242',        amount: 500.00,   time: '2026-07-08T08:20:00Z', status: 'completed' },
  { id: '7', kind: 'challenge', title: 'Beginner $50K challenge',       sub: 'Order ord-1',          amount: -79.00,   time: '2026-07-04T11:00:00Z', status: 'completed' },
];

const KIND_ICON: Record<string, { bg: string; icon: string }> = {
  deposit:   { bg: 'bg-[#34C759]/15', icon: '💳' },
  withdraw:  { bg: 'bg-[#FF9500]/15', icon: '↗' },
  challenge: { bg: 'bg-tf-text-1/15', icon: '🎯' },
  reward:    { bg: 'bg-[#FF9500]/15', icon: '🎁' },
  payout:    { bg: 'bg-[#34C759]/15', icon: '💰' },
};

export function WalletPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState<'all' | 'deposits' | 'withdrawals' | 'payouts'>('all');
  const available = 12480.20;
  const pending = 1200.00;
  const total = available + pending;

  const filtered = DEMO_TXNS.filter((t) => {
    if (tab === 'all') return true;
    if (tab === 'deposits') return t.kind === 'deposit';
    if (tab === 'withdrawals') return t.kind === 'withdraw';
    if (tab === 'payouts') return t.kind === 'payout' || t.kind === 'reward';
    return true;
  });

  return (
    <div className="min-h-screen bg-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Wallet</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
        </button>
      </div>

      {/* Balance hero */}
      <div className="mx-4 mt-4 overflow-hidden rounded-[20px] bg-gradient-to-br from-[#1C1C1E] to-[#2A2A2A] p-5 text-white">
        <div className="text-[12px] uppercase tracking-wide text-white/60">Total balance</div>
        <div className="mt-1 text-[40px] font-bold tabular-nums tracking-tight">${total.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
        <div className="mt-3 grid grid-cols-2 gap-3 border-t border-white/10 pt-3">
          <div>
            <div className="text-[11px] text-white/60">Available</div>
            <div className="text-[16px] font-semibold tabular-nums">${available.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[11px] text-white/60">Pending</div>
            <div className="text-[16px] font-semibold tabular-nums text-tf-warning">${pending.toLocaleString()}</div>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <Action onClick={() => nav('/portal/wallet/deposit')} icon="💳" label="Deposit" />
          <Action onClick={() => nav('/portal/wallet/withdraw')} icon="↗" label="Withdraw" />
          <Action onClick={() => nav('/portal/discover')} icon="🎯" label="Activate" />
        </div>
      </div>

      {/* Payment methods */}
      <h2 className="mt-6 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Payment methods</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <button onClick={() => nav('/portal/wallet/methods')} className="flex w-full items-center gap-3 border-b border-tf-border px-4 py-3 active:bg-[#E5E5EA]/60">
          <div className="flex h-9 w-12 items-center justify-center rounded-[6px] bg-gradient-to-br from-[#1A1F71] to-[#1A1F71] text-[10px] font-bold text-white">VISA</div>
          <div className="flex-1 text-left">
            <div className="text-[14px] font-semibold text-tf-text-1">Visa •••• 4242</div>
            <div className="text-[11px] text-tf-text-3">Expires 12/27 · Default</div>
          </div>
          <span className="rounded-full bg-[#34C759]/15 px-2 py-0.5 text-[10px] font-semibold text-tf-profit">Default</span>
        </button>
        <button onClick={() => nav('/portal/wallet/methods')} className="flex w-full items-center gap-3 px-4 py-3 active:bg-[#E5E5EA]/60">
          <div className="flex h-9 w-12 items-center justify-center rounded-[6px] bg-tf-surface-1 text-tf-text-1">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" strokeLinecap="round" /></svg>
          </div>
          <div className="flex-1 text-left">
            <div className="text-[14px] font-semibold text-tf-text-1">Add new method</div>
            <div className="text-[11px] text-tf-text-3">Card, bank, USDT, BTC</div>
          </div>
          <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* Transactions */}
      <h2 className="mt-6 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Transactions</h2>
      <div className="mx-4 flex items-center gap-1.5 overflow-x-auto">
        {(['all', 'deposits', 'withdrawals', 'payouts'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`txn-tab-${t}`}
            className={['shrink-0 rounded-full px-3 py-1 text-[12px] font-semibold capitalize',
              tab === t ? 'bg-tf-text-1 text-white' : 'bg-tf-surface-1 text-tf-text-1'].join(' ')}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="mx-4 mt-3 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-[14px] text-tf-text-3">No transactions yet</div>
        ) : filtered.map((t, i) => {
          const meta = KIND_ICON[t.kind];
          return (
            <div key={t.id} data-testid={`txn-${t.id}`} className={['flex items-center gap-3 px-4 py-3', i < filtered.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
              <div className={['flex h-10 w-10 items-center justify-center rounded-[10px] text-[18px]', meta.bg].join(' ')}>{meta.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-tf-text-1 truncate">{t.title}</div>
                <div className="text-[11px] text-tf-text-3">{t.sub} · {new Date(t.time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
              </div>
              <div className="text-right">
                <div className={['text-[14px] font-semibold tabular-nums', t.amount >= 0 ? 'text-tf-profit' : 'text-tf-text-1'].join(' ')}>
                  {t.amount >= 0 ? '+' : ''}${Math.abs(t.amount).toFixed(2)}
                </div>
                <div className={['text-[10px] font-semibold uppercase',
                  t.status === 'completed' ? 'text-tf-profit' : t.status === 'pending' ? 'text-tf-warning' : 'text-tf-loss'].join(' ')}>
                  {t.status}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Action({ onClick, icon, label }: { onClick: () => void; icon: string; label: string }) {
  return (
    <button
      onClick={onClick}
      data-testid={`wallet-${label.toLowerCase()}`}
      className="flex flex-col items-center gap-1 rounded-[14px] bg-white/10 py-3 active:bg-white/20"
    >
      <span className="text-[18px]">{icon}</span>
      <span className="text-[12px] font-semibold">{label}</span>
    </button>
  );
}
